import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../../utils/api";

// Types
interface BoxFile {
  file_id: string;
  file_name: string;
  file_type: string;
  folder_name: string;
}

interface CreateAndSyncState {
  loading: boolean;
  error: string | null;
  tsMessage: string | null;
  syncMessage: string | null;
  syncedFiles: BoxFile[];
  inserted: number;
  removed: number;
}

const initialState: CreateAndSyncState = {
  loading: false,
  error: null,
  tsMessage: null,
  syncMessage: null,
  syncedFiles: [],
  inserted: 0,
  removed: 0,
};

// Thunks

// Create TS folder
export const createTSFolder = createAsyncThunk<
  string,
  { boxFolderId: string; patientFolderName: string; caseId?: string },
  { rejectValue: string }
>("createAndSync/createTSFolder", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.post(
      `/api/create/tsfolder`,
      payload
    );
    return res.data.message;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message);
  }
});

// Sync Files
export const syncFiles = createAsyncThunk<
  {
    syncedFiles: BoxFile[];
    inserted: number;
    removed: number;
    message: string;
  },
  { userId: string; caseId: string; boxFolderId: string, uploadBy: string },
  { rejectValue: string }
>("createAndSync/syncFiles", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.post(
      `/api/sync/files`,
      payload
    );

    return {
      syncedFiles: res.data.allFiles,
      inserted: res.data.inserted,
      removed: res.data.removed,
      message: res.data.message,
    };
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message);
  }
});

// Slice
const createAndSyncSlice = createSlice({
  name: "createAndSync",
  initialState,
  reducers: {
    clearMessages(state) {
      state.tsMessage = null;
      state.syncMessage = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Create TS Folder
    builder.addCase(createTSFolder.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.tsMessage = null;
    });
    builder.addCase(
      createTSFolder.fulfilled,
      (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.tsMessage = action.payload;
      }
    );
    builder.addCase(createTSFolder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to create TS folder";
    });

    // Sync Files
    builder.addCase(syncFiles.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.syncMessage = null;
    });
    builder.addCase(syncFiles.fulfilled, (state, action) => {
      state.loading = false;
      state.syncedFiles = action.payload.syncedFiles;
      state.inserted = action.payload.inserted;
      state.removed = action.payload.removed;
      state.syncMessage = action.payload.message;
    });
    builder.addCase(syncFiles.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to sync files";
    });
  },
});

export const { clearMessages } = createAndSyncSlice.actions;
export default createAndSyncSlice.reducer;
